// src/utils/shareCard.ts
//
// Two-step share flow:
//   1. Publish a minimal copy of the card to /shared_cards/{shareId}
//      (world-readable). This is what the share-recipient will fetch.
//   2. Fire the OS share sheet via navigator.share, falling back to
//      clipboard copy if Web Share isn't available.
//
// The share URL points to https://instant.tours/guide/{shareId}. The
// public route renders the shared card with rich OG metadata for
// preview unfurls (Slack, iMessage, Twitter, etc.).
//
// Two payload kinds supported:
//   - shareCard(GuideFeedCard) — guide feed cards
//   - sharePostcard(group, hero) — postcard / visit groups

import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../services/firebase';
import type { GuideFeedCard } from '../hooks/useGuideFeed';
import type { PostcardPoi } from '../components/postcards/PostcardGroup';

/** Origin used for share URLs. Defaults to current location. */
function shareOrigin(): string {
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin;
  }
  return 'https://instant.tours';
}

/** Generate a short, URL-safe share id from the card. We base it on
 *  the card's id but trim to keep it shareable. Collisions are
 *  vanishingly unlikely because card ids embed timestamp + slug. */
function makeShareId(cardId: string): string {
  // Card ids are already URL-safe (kind_slug_b36ts). Trim if absurdly long.
  return cardId.length > 80 ? cardId.slice(0, 80) : cardId;
}

/** Publish (or republish) a card's content to /shared_cards/{shareId}.
 *  This is world-readable — no PII, no user-specific signals. */
async function publishSharedCard(
  card: GuideFeedCard,
  shareId: string
): Promise<void> {
  const user = auth.currentUser;
  // Even anonymous users have a Firebase user — guard anyway.
  if (!user) throw new Error('Must be signed in to share');

  const payload: Record<string, any> = {
    id: shareId,
    kind: card.kind,
    title: card.title,
    body: card.body,
    meta: card.meta || null,
    place_name: card.place_name || null,
    photo_url: card.photo_url || null,
    location: card.location || null,
    tags: card.tags || [],
    list_items: card.list_items || null,
    for_city: card.for_city || null,
    for_country: card.for_country || null,
    shared_by_uid: user.uid,
    shared_at: serverTimestamp(),
  };
  // Strip nulls so we don't fight Firestore's no-undefined rule via
  // shape mismatch.
  const clean: Record<string, any> = {};
  for (const [k, v] of Object.entries(payload)) {
    if (v !== undefined) clean[k] = v;
  }

  await setDoc(doc(db, 'shared_cards', shareId), clean, { merge: true });
}

/** Build the human-readable text body for the share. Different per
 *  kind — places get a "X in Y" framing, lists get the list, etc. */
function buildShareText(card: GuideFeedCard): { title: string; text: string } {
  const cityTag = card.for_city ? ` · ${card.for_city}` : '';

  switch (card.kind) {
    case 'place':
      return {
        title: `${card.title}${cityTag}`,
        text: card.body,
      };
    case 'event':
      return {
        title: `${card.title}${card.meta ? ' — ' + card.meta : ''}`,
        text: card.body,
      };
    case 'list': {
      const items = (card.list_items || [])
        .map((e, i) => `${i + 1}. ${e.title}`)
        .join('\n');
      return {
        title: `${card.title}${cityTag}`,
        text: `${card.body}\n\n${items}`,
      };
    }
    case 'tip':
      return {
        title: `Local tip${cityTag}`,
        text: `${card.title}\n\n${card.body}`,
      };
    case 'story':
      return {
        title: card.title,
        text: card.body,
      };
    case 'prompt':
      return {
        title: card.title,
        text: card.body,
      };
    case 'suggestion':
      return {
        title: card.title,
        text: card.body,
      };
    default:
      return { title: card.title, text: card.body };
  }
}

/** Result of a share attempt. */
export type ShareResult =
  | { method: 'native'; success: true }
  | { method: 'clipboard'; success: true }
  | { method: 'cancelled'; success: false }
  | { method: 'error'; success: false; error: string };

/**
 * Share a guide card via the OS native share sheet, falling back to
 * clipboard copy when Web Share isn't available.
 *
 * Steps:
 *   1. Publish the card to /shared_cards/{id} (world-readable mirror)
 *   2. Fire navigator.share OR copy URL to clipboard
 *   3. Return the outcome so the caller can show feedback
 *
 * Network failures during publish degrade gracefully — we still try
 * to share the URL even if the publish call hiccups, because the
 * shared_cards doc may already exist from a prior share.
 */
export async function shareCard(card: GuideFeedCard): Promise<ShareResult> {
  const shareId = makeShareId(card.id);
  const url = `${shareOrigin()}/guide/${shareId}`;

  // 1. Publish. Best-effort.
  try {
    await publishSharedCard(card, shareId);
  } catch (err) {
    console.warn('shareCard: publish failed (continuing anyway):', err);
  }

  const { title, text } = buildShareText(card);

  // 2. Try Web Share API
  const nav = typeof navigator !== 'undefined' ? navigator : null;
  if (nav?.share) {
    try {
      await nav.share({
        title,
        text,
        url,
      });
      return { method: 'native', success: true };
    } catch (err: any) {
      // AbortError = user cancelled the sheet
      if (err?.name === 'AbortError') {
        return { method: 'cancelled', success: false };
      }
      // Fall through to clipboard
      console.warn('shareCard: navigator.share failed:', err);
    }
  }

  // 3. Fallback — copy URL to clipboard
  try {
    if (nav?.clipboard?.writeText) {
      await nav.clipboard.writeText(url);
      return { method: 'clipboard', success: true };
    }
  } catch (err) {
    console.warn('shareCard: clipboard write failed:', err);
  }

  return {
    method: 'error',
    success: false,
    error: 'Sharing not available on this device.',
  };
}

// ────────────────────────────────────────────────────────────
// Postcard sharing
// ────────────────────────────────────────────────────────────

/** Make a stable share id from a postcard group. We use the
 *  fuzzy-grouping key so resharing the same postcard doesn't create
 *  a new doc each time. */
function makePostcardShareId(group: PostcardPoi): string {
  const slug = group.key.replace(/[^a-z0-9-_]/gi, '').slice(0, 40) || 'visit';
  // postcard_ prefix to avoid collisions with guide card ids
  return `postcard_${slug}`;
}

/** Pick the best photo URL for a postcard. The visits each carry a
 *  Google Places photo_reference; we pick the most recent one that
 *  has a reference and resolve it via the same proxy used elsewhere. */
function pickPostcardPhoto(group: PostcardPoi): string | null {
  for (const v of group.visits) {
    if (v.photo_reference) {
      // Google Places photo URL via Places Photo API endpoint
      return `https://maps.googleapis.com/maps/api/place/photo?maxwidth=800&photoreference=${encodeURIComponent(
        v.photo_reference
      )}&key=${
        (import.meta as any).env?.VITE_GOOGLE_MAPS_BROWSER_KEY || ''
      }`;
    }
  }
  return null;
}

function buildPostcardShareText(group: PostcardPoi): {
  title: string;
  text: string;
} {
  const visitCount = group.visits.length;
  const visitsLine =
    visitCount === 1
      ? "I've been here."
      : `I've been here ${visitCount} times.`;
  const newest = group.visits[0];
  const newestDate = newest?.verified_at
    ? newest.verified_at.toLocaleDateString(undefined, {
        month: 'long',
        year: 'numeric',
      })
    : null;

  return {
    title: group.displayName,
    text: newestDate
      ? `${visitsLine} Most recent: ${newestDate}.`
      : visitsLine,
  };
}

/** Publish a postcard to /shared_cards/{shareId} as kind=postcard. */
async function publishPostcard(
  group: PostcardPoi,
  shareId: string,
  photoUrl: string | null
): Promise<void> {
  const user = auth.currentUser;
  if (!user) throw new Error('Must be signed in to share');

  const newest = group.visits[0];
  const visitCount = group.visits.length;
  const newestDate = newest?.verified_at
    ? newest.verified_at.toISOString()
    : null;

  const body =
    visitCount === 1
      ? `Verified visit to ${group.displayName}${newestDate ? ` on ${new Date(newestDate).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' })}` : ''}.`
      : `${visitCount} visits to ${group.displayName} captured in Instant Tours, the most recent ${newestDate ? new Date(newestDate).toLocaleDateString(undefined, { month: 'long', year: 'numeric' }) : 'recently'}.`;

  const payload: Record<string, any> = {
    id: shareId,
    kind: 'postcard',
    title: group.displayName,
    body,
    place_name: group.displayName,
    photo_url: photoUrl,
    location:
      newest?.location?.latitude !== undefined &&
      newest?.location?.longitude !== undefined
        ? { lat: newest.location.latitude, lng: newest.location.longitude }
        : null,
    tags: [group.type],
    visit_count: visitCount,
    most_recent_visit_at: newestDate,
    shared_by_uid: user.uid,
    shared_at: serverTimestamp(),
  };
  const clean: Record<string, any> = {};
  for (const [k, v] of Object.entries(payload)) {
    if (v !== undefined && v !== null) clean[k] = v;
  }

  await setDoc(doc(db, 'shared_cards', shareId), clean, { merge: true });
}

/** Share a postcard via the OS native share sheet. */
export async function sharePostcard(group: PostcardPoi): Promise<ShareResult> {
  const shareId = makePostcardShareId(group);
  const url = `${shareOrigin()}/guide/${shareId}`;
  const photoUrl = pickPostcardPhoto(group);

  try {
    await publishPostcard(group, shareId, photoUrl);
  } catch (err) {
    console.warn('sharePostcard: publish failed (continuing anyway):', err);
  }

  const { title, text } = buildPostcardShareText(group);

  const nav = typeof navigator !== 'undefined' ? navigator : null;
  if (nav?.share) {
    try {
      await nav.share({ title, text, url });
      return { method: 'native', success: true };
    } catch (err: any) {
      if (err?.name === 'AbortError') {
        return { method: 'cancelled', success: false };
      }
      console.warn('sharePostcard: navigator.share failed:', err);
    }
  }

  try {
    if (nav?.clipboard?.writeText) {
      await nav.clipboard.writeText(url);
      return { method: 'clipboard', success: true };
    }
  } catch (err) {
    console.warn('sharePostcard: clipboard write failed:', err);
  }

  return {
    method: 'error',
    success: false,
    error: 'Sharing not available on this device.',
  };
}
